import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CardActions from "@mui/material/CardActions";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import GennevApiService from "../../services/gennev-api.service";
import Alert from "@mui/material/Alert";

import { Box, Stack } from "@mui/material";

export default () => {
  const timeStartConfig = { hour: 0, minute: 0, second: 1, millisecond: 0 };
  const timeEndConfig = { hour: 23, minute: 59, second: 59, millisecond: 999 };

  const [userStats, setUserStats] = useState(() => ({}));
  const [medicalStats, setMedicalStats] = useState(() => ({}));
  const [appointmentStats, setAppointmentStats] = useState(() => ({}));
  const [start, setStart] = useState(() =>
    moment().set(timeStartConfig).subtract(7, "days")
  );
  const [end, setEnd] = useState(() => moment().set(timeEndConfig));
  const [error, setError] = useState(() => ({
    error: false,
  }));
  const [inProgress, setInProgress] = useState(false);
  const [indexError, setIndexError] = useState(() => ({
    error: false,
  }));

  const cleanError = () => setError(() => ({ error: false }));

  const handleGetStats = (start, end) => {
    const startDate = moment(start).set(timeStartConfig);
    const endDate = moment(end).set(timeStartConfig);

    if (endDate.diff(startDate) < 0)
      return setError(() => ({
        error: true,
        message: "Initial date must be lower than end date",
      }));
    else cleanError();

    GennevApiService.getMedicalAppointmentStats(
      startDate.toISOString(),
      endDate.toISOString()
    )
      .then(({ data }) => {
        setAppointmentStats(data);
        cleanError();
      })
      .catch((e) => {
        setError(() => ({
          error: true,
          message: e.toString() || "Error",
        }));
      });

    GennevApiService.getUserStats(
      startDate.toISOString(),
      endDate.toISOString()
    )
      .then(({ data }) => {
        setUserStats(data);
        cleanError();
      })
      .catch((e) => {
        setError(() => ({
          error: true,
          message: e.toString() || "Error",
        }));
      });
  };

  const reindexCollection = async (collection) => {
    setInProgress(true);
    await GennevApiService.reindex(collection)
      .then(() => setIndexError({ error: false }))
      .catch(() =>
        setIndexError({ error: true, message: `Error indexing ${collection}` })
      )
      .finally(() => setInProgress(false));
  };

  useEffect(() => {
    handleGetStats(start, end);
  }, [start, end]);

  return (
    <div>
      <Card>
        <CardHeader title="Admin dashboard" />
        <CardContent>Welcome to the Gennev admin panel.</CardContent>
      </Card>

      <Card style={{ marginTop: "30px" }}>
        {error.error && <Alert severity="error">{error.message}</Alert>}

        <CardActions>
          <Grid container justifyContent="flex-start" alignItems="flex-end">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={start}
                style={{ marginLeft: ".8rem" }}
                margin="normal"
                id="start"
                label="Date From"
                onChange={setStart}
                format="MM/DD/YYYY"
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
              <DatePicker
                value={end}
                style={{ marginLeft: ".8rem" }}
                margin="normal"
                id="end"
                label="Date To"
                onChange={setEnd}
                format="MM/DD/YYYY"
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="secondary"
              style={{ marginBottom: ".5rem", marginLeft: ".8rem" }}
              onClick={() => {
                handleGetStats(start, end);
              }}
            >
              Get Stats
            </Button>
          </Grid>
        </CardActions>

        <Grid container justifyContent="flex-start">
          <div>
            <CardHeader title="User Stats" />
            <CardContent>
              Total new registered users: {userStats.count}
            </CardContent>
            <CardContent>Cumulative total: {userStats.total}</CardContent>
            <CardContent>
              Total new registered providers: {userStats.newProviders}
            </CardContent>
            <CardContent>
              New activated users: {userStats.activatedUsers}
            </CardContent>
          </div>
          <div>
            <CardHeader title="Appointment Stats" />
            <Stack direction="row">
              <Box>
                <CardContent>
                  Patients: {appointmentStats.totalPatientsOnAppointments}
                </CardContent>
                <CardContent>
                  First time appointments:{" "}
                  {appointmentStats.totalFirstTimeAppointments}
                </CardContent>
                <CardContent>
                  Appointments: {appointmentStats.totalAppointments}
                </CardContent>
                <CardContent>
                  Telehealth appointments:{" "}
                  {appointmentStats.totalTelehealthAppointments}
                </CardContent>
              </Box>
              <Box>
                <CardContent>
                  Coach appointments: {appointmentStats.totalCoachAppointments}
                </CardContent>
                <CardContent>
                  Cumulative total: {appointmentStats.cumulativeTotal}
                </CardContent>
                <CardContent>
                  Cumulative telehealth total:{" "}
                  {appointmentStats.cumulativeTelehealthTotal}
                </CardContent>
                <CardContent>
                  Cumulative coach total:{" "}
                  {appointmentStats.cumulativeCoachTotal}
                </CardContent>
              </Box>
            </Stack>
          </div>
        </Grid>
      </Card>
      <Card style={{ marginTop: "30px" }}>
        <CardHeader title="Algolia Indexes" />
        {indexError.error && (
          <Alert severity="error">{indexError.message}</Alert>
        )}

        <Grid container justifyContent="flex-start">
          <div>
            <CardContent>
              <Button
                variant="contained"
                color="secondary"
                style={{ marginBottom: ".5rem", marginLeft: ".8rem" }}
                disabled={inProgress}
                onClick={() => {
                  reindexCollection("articles");
                }}
              >
                Reindex Articles
              </Button>
            </CardContent>
          </div>
          <div>
            <CardContent>
              <Button
                variant="contained"
                color="secondary"
                style={{ marginBottom: ".5rem", marginLeft: ".8rem" }}
                disabled={inProgress}
                onClick={() => {
                  reindexCollection("press-items");
                }}
              >
                Reindex Press Items
              </Button>
            </CardContent>
          </div>
        </Grid>
      </Card>
    </div>
  );
};
